import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { SelectionChangedEvent } from 'devextreme/ui/lookup';
import { GenericCrudEditComponent } from '../../../../../libs/components/generic-crud/generic-crud-edit.component';
import {Checkpoint, ECheckpointDataType, TId, WorkProcedureStep} from '../../../../../models';
import {firstValueFrom, merge, of} from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  applyCheckpointValuesFromLookupItem,
  normalizeCheckpointValueList,
  stepHasCheckpoint,
  syncDefaultValuesControlWithChoices,
  syncRequiredControlWithCheckpoint,
} from '../work-procedure-step-default-values';

@Component({
  selector: 'app-steps-edit',
  templateUrl: './steps-edit.component.html',
  styleUrl: './steps-edit.component.sass',
  standalone: false,
})
export class StepsEditComponent extends GenericCrudEditComponent<WorkProcedureStep> {
  checkpoints: Checkpoint[] = [];

  private readonly cdr = inject(ChangeDetectorRef);
  protected readonly String = String;

  defaultValuesChoices: string[] = [];

  currentCheckpointType:number = 0;


  override displayEntity(): string {
    return this.entity!.instructions ?? '';
  }

  protected override async onInitExtendActions(): Promise<void> {
    this.checkpoints = await firstValueFrom( this.api.resources.checkpoints.getAll())
    const checkpointValuesCtrl = this.form!.get('checkpointValues')!;
    const defaultValuesCtrl = this.form!.get('defaultValues')!;
    merge(of(checkpointValuesCtrl.value), checkpointValuesCtrl.valueChanges)
      .pipe(
        takeUntil(this.destroy$),
        map(v => normalizeCheckpointValueList(v as string[] | null | undefined)),
      )
      .subscribe(choices => {
        this.defaultValuesChoices = choices;
        syncDefaultValuesControlWithChoices(defaultValuesCtrl, choices);
        this.cdr.markForCheck();
      });

    syncRequiredControlWithCheckpoint(this.form!, this.destroy$, this.cdr);
  }

  onCheckpointSelectionChanged(e: SelectionChangedEvent): void {
    const cvCtrl = this.form?.get('checkpointValues');
    if (!cvCtrl) {
      return;
    }
    this.currentCheckpointType = e.selectedItem.dataTypeId;
    applyCheckpointValuesFromLookupItem(cvCtrl, e.selectedItem);
  }

  initFormGroup(): FormGroup {
    const e = this.entity!;
    const firstDefaultValue = e.defaultValues?.[0] ?? null;

    return this.fb.group({
      id: [e.id],
      checkpointId: [e.checkpointId ?? null],
      checkpointValues: [normalizeCheckpointValueList(e.checkpointValues)],
      order: [e.order ?? 0],
      instructions: [e.instructions ?? ''],
      instructionsValue: [e.instructionsValue ?? null],

      defaultValues: [e.defaultValues ?? []],

      defaultTextValue: [firstDefaultValue],
      defaultLongTextValue: [firstDefaultValue],
      defaultNumericValue: [firstDefaultValue != null ? Number(firstDefaultValue) : null],
      defaultDateValue: [firstDefaultValue],
      defaultTimeValue: [firstDefaultValue],
      defaultBooleanValue: [firstDefaultValue === 'true' || (firstDefaultValue as any) === true],

      required: [!!e.required],
    });
  }

  protected override async mapFormEntity(): Promise<Partial<WorkProcedureStep>> {
    const v = this.form!.value as Record<string, unknown>;

    let arr: string[] = [];

    if (this.isSelectionType) {
      let defaultValues = v['defaultValues'];

      if (defaultValues != null && !Array.isArray(defaultValues)) {
        defaultValues = [defaultValues];
      }

      arr = Array.isArray(defaultValues)
        ? (defaultValues as unknown[])
          .filter(x => x != null && x !== '')
          .map(x => String(x))
        : [];

    } else if (this.isTextType) {
      arr = v['defaultTextValue']
        ? [String(v['defaultTextValue'])]
        : [];

    } else if (this.isLongTextType) {
      arr = v['defaultLongTextValue']
        ? [String(v['defaultLongTextValue'])]
        : [];

    } else if (this.isNumericType) {
      arr =
        v['defaultNumericValue'] !== null &&
        v['defaultNumericValue'] !== undefined &&
        v['defaultNumericValue'] !== ''
          ? [String(v['defaultNumericValue'])]
          : [];

    } else if (this.isDateType) {
      arr = v['defaultDateValue']
        ? [String(v['defaultDateValue'])]
        : [];

    } else if (this.isTimeType) {
      arr = v['defaultTimeValue']
        ? [String(v['defaultTimeValue'])]
        : [];

    } else if (this.isBooleanType) {
      arr = [String(!!v['defaultBooleanValue'])];
    }

    const instructionsValue = v['instructionsValue'];
    const checkpointId = (v['checkpointId'] as string | null | undefined) ?? undefined;

    return {
      id: v['id'] as TId,
      checkpointId,
      order: Number(v['order'] ?? 0) || 0,
      required: stepHasCheckpoint(checkpointId) && !!v['required'],
      instructions: String(v['instructions'] ?? ''),
      instructionsValue:
        instructionsValue == null || instructionsValue === ''
          ? 0
          : Number(instructionsValue),
      defaultValues: arr,
    };
  }

  get isBooleanType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Boolean;
  }

  get isTextType(): boolean {
    return (this.currentCheckpointType === ECheckpointDataType.Text || this.currentCheckpointType === ECheckpointDataType.Sensor);
  }

  get isLongTextType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.LongText;
  }

  get isNumericType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Numeric;
  }

  get isDateType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Date;
  }

  get isTimeType(): boolean {
    return this.currentCheckpointType === ECheckpointDataType.Time;
  }

  get isSelectionType(): boolean {
    return (this.currentCheckpointType === ECheckpointDataType.UniqueSelection || this.currentCheckpointType === ECheckpointDataType.MultipleSelection) ;
  }
}
